import React, { useState, useEffect } from 'react';
import { useStripe, useElements, PaymentElement, Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';

// Load the Stripe instance once
// Use environment variable from Vite
let stripePromise: Promise<any> | null = null;

const getStripe = async () => {
  if (!stripePromise) {
    if (!import.meta.env.VITE_STRIPE_PUBLIC_KEY) {
      // Fallback to API if environment variable isn't directly accessible
      try {
        const envResponse = await fetch('/api/env');
        const env = await envResponse.json();
        
        if (!env.VITE_STRIPE_PUBLIC_KEY) {
          throw new Error('Missing Stripe public key');
        }
        
        stripePromise = loadStripe(env.VITE_STRIPE_PUBLIC_KEY);
      } catch (err) {
        console.error('Failed to load Stripe key:', err);
        throw new Error('Could not initialize Stripe');
      }
    } else {
      // Use the environment variable directly if available
      stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY);
    }
  }
  return stripePromise;
};

// The checkout form component
function CheckoutForm({ 
  onPaymentSuccess, 
  onCancel,
  screenplayId,
  amount,
  title
}: { 
  onPaymentSuccess: (paymentIntentId: string) => void;
  onCancel: () => void;
  screenplayId: string;
  amount: number;
  title: string;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { toast } = useToast();
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      // Stripe.js hasn't loaded yet
      return;
    }
    
    setIsLoading(true);
    setErrorMessage(null);
    
    try {
      // Confirm payment and handle result
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
        confirmParams: {
          return_url: window.location.href, // In case we need redirection
        },
      });
      
      if (error) {
        setErrorMessage(error.message || 'An error occurred during payment processing');
        toast({
          title: 'Payment failed',
          description: error.message || 'An error occurred during payment processing',
          variant: 'destructive',
        });
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        // Payment was successful
        toast({
          title: 'Payment successful',
          description: 'Your payment has been processed successfully',
        });
        onPaymentSuccess(paymentIntent.id);
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'An unexpected error occurred');
      toast({
        title: 'Payment error',
        description: err.message || 'An unexpected error occurred',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <form onSubmit={handleSubmit}>
      <div className="space-y-6">
        <PaymentElement />
        
        {errorMessage && (
          <div className="p-3 bg-destructive/15 text-destructive rounded-md">
            {errorMessage}
          </div>
        )}
        
        <div className="flex justify-end space-x-4">
          <Button 
            type="button" 
            variant="outline" 
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            disabled={!stripe || isLoading}
            className="min-w-[150px]"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              `Pay ${new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
              }).format(amount / 100)}`
            )}
          </Button>
        </div>
      </div>
    </form>
  );
}

// The wrapper component that loads Stripe and displays the payment UI
export default function StripePayment({
  screenplayId,
  amount,
  title,
  onPaymentSuccess,
  onCancel
}: {
  screenplayId: string;
  amount: number;
  title: string;
  onPaymentSuccess: (paymentIntentId: string) => void;
  onCancel: () => void;
}) {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  
  // Get a payment intent from the server when the component loads
  useEffect(() => {
    const createPaymentIntent = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const response = await apiRequest('POST', '/api/create-payment-intent', {
          screenplayId,
        });
        
        const data = await response.json();
        
        if (data.error) {
          throw new Error(data.error);
        }
        
        setClientSecret(data.clientSecret);
      } catch (err: any) {
        setError(err.message || 'Failed to initialize payment');
        toast({
          title: 'Payment initialization failed',
          description: err.message || 'Failed to initialize payment',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    createPaymentIntent();
  }, [screenplayId, toast]);
  
  return (
    <Card className="w-full max-w-lg mx-auto">
      <CardHeader>
        <CardTitle>Complete Your Purchase</CardTitle>
        <CardDescription>
          Secure payment for rendering "{title}"
        </CardDescription>
      </CardHeader>
      
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-2">Initializing payment...</span>
          </div>
        ) : error ? (
          <div className="p-4 bg-destructive/15 text-destructive rounded-md">
            <p className="font-medium">Error</p>
            <p>{error}</p>
            <Button className="mt-4" variant="outline" onClick={onCancel}>
              Go Back
            </Button>
          </div>
        ) : clientSecret ? (
          <Elements stripe={getStripe()} options={{ clientSecret }}>
            <CheckoutForm 
              screenplayId={screenplayId}
              amount={amount}
              title={title}
              onPaymentSuccess={onPaymentSuccess} 
              onCancel={onCancel} 
            />
          </Elements>
        ) : (
          <div className="p-4 bg-amber-100 text-amber-800 rounded-md">
            <p>Unable to initialize payment. Please try again later.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
